import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import AbaProtegida from '../components/common/AbaProtegida';
import { ConfiguracoesService, Configuracao, Conferente } from '../services/configuracoesService';
import { AvisosService, Aviso } from '../services/avisosService';
import MotivosOcorrenciaService, { MotivoOcorrencia } from '../services/motivosOcorrenciaServiceAPI';
import { usePermissoes } from '../hooks/usePermissoes';

const Configuracoes: React.FC = () => {
  const { user } = useAuth();
  const { filialAtual, setFilialAtual } = useApp();
  const { temPermissao: podeGerenciarNotificacoes } = usePermissoes('configuracoes', 'gerenciar_notificacoes');
  const { temPermissao: podeGerenciarTermosRegras } = usePermissoes('configuracoes', 'gerenciar_termos_regras');
  const { temPermissao: podeGerenciarAvisos } = usePermissoes('configuracoes', 'gerenciar_avisos');
  const { temPermissao: podeGerenciarConferentes } = usePermissoes('configuracoes', 'gerenciar_conferentes');
  const { temPermissao: podeGerenciarMotivosOcorrencia } = usePermissoes('configuracoes', 'gerenciar_motivos_ocorrencia');

  const filiais = [
    { id: 'FILIAL_1', nome: 'Filial 1 - UP Distribuidora de Produtos e Descartáveis LTDA' },
    { id: 'FILIAL_2', nome: 'Filial 2 - Aleplast Comércio de Produtos e Descartáveis LTDA' },
    { id: 'FILIAL_3', nome: 'Filial 3 - Alepack Industria e Comercio Plasticos LTDA' }
  ];

  // Fallback para admin - acesso total a todas as configurações
  const isAdmin = user?.perfil === 'admin';
  const podeVerNotificacoes = isAdmin || podeGerenciarNotificacoes;
  const podeVerTermosRegras = isAdmin || podeGerenciarTermosRegras;
  const podeVerAvisos = isAdmin || podeGerenciarAvisos;
  const podeVerConferentes = isAdmin || podeGerenciarConferentes;
  const podeVerMotivosOcorrencia = isAdmin || podeGerenciarMotivosOcorrencia;
  const [abaAtiva, setAbaAtiva] = useState<'gerais' | 'cargas'>('gerais');
  const [subAbaGeral, setSubAbaGeral] = useState<'notificacoes' | 'termos_regras' | 'avisos'>('notificacoes');
  const [subAbaCargas, setSubAbaCargas] = useState<'conferentes' | 'motivos'>('conferentes');
  const [loading, setLoading] = useState(false);

  // Configurações
  const [configuracoes, setConfiguracoes] = useState<Configuracao[]>([]);
  const [notificacoesHabilitadas, setNotificacoesHabilitadas] = useState(true);
  const [tiposNotificacoes, setTiposNotificacoes] = useState('');
  const [termosRegrasTitulo, setTermosRegrasTitulo] = useState('');
  const [termosRegrasTexto, setTermosRegrasTexto] = useState('');
  const [postosFiscais, setPostosFiscais] = useState('');
  const [horarioFuncionamento, setHorarioFuncionamento] = useState('Segunda a Sexta: 08:00 às 18:00');
  const [horarioRecebimentoNF, setHorarioRecebimentoNF] = useState('Segunda a Sexta: 08:00 às 17:00');

  // Motivos de Ocorrência
  const [motivosOcorrencia, setMotivosOcorrencia] = useState<MotivoOcorrencia[]>([]);
  const [showModalMotivo, setShowModalMotivo] = useState(false);
  const [editandoMotivo, setEditandoMotivo] = useState<MotivoOcorrencia | null>(null);
  const [motivoNome, setMotivoNome] = useState('');
  const [motivoDescricao, setMotivoDescricao] = useState('');

  // Conferentes
  const [conferentes, setConferentes] = useState<Conferente[]>([]);
  const [showModalConferente, setShowModalConferente] = useState(false);
  const [editandoConferente, setEditandoConferente] = useState<Conferente | null>(null);
  const [conferenteNome, setConferenteNome] = useState('');
  const [conferenteFilial, setConferenteFilial] = useState('');

  // Avisos
  const [avisos, setAvisos] = useState<Aviso[]>([]);
  const [showModalAviso, setShowModalAviso] = useState(false);
  const [editandoAviso, setEditandoAviso] = useState<Aviso | null>(null);
  const [avisoTitulo, setAvisoTitulo] = useState('');
  const [avisoMensagem, setAvisoMensagem] = useState('');
  const [avisoLado, setAvisoLado] = useState<'esquerdo' | 'direito'>('esquerdo');
  const [avisoData, setAvisoData] = useState(new Date().toISOString().split('T')[0]);
  const [avisoStatus, setAvisoStatus] = useState<'ativo' | 'inativo'>('ativo');
  
  useEffect(() => {
    carregarConfiguracoes();
    carregarMotivosOcorrencia();
    carregarConferentes();
    carregarAvisos();
    carregarTermosRegras(filialAtual);
  }, [filialAtual]);

  const carregarConfiguracoes = async () => {
    try {
      setLoading(true);
      const dados = await ConfiguracoesService.buscarTodas();
      setConfiguracoes(dados);
      
      // Carregar valores específicos (globais, sem filial)
      const notifHabilitadas = dados.find(c => c.chave === 'notificacoes_habilitadas' && !c.filial);
      const notifTipos = dados.find(c => c.chave === 'notificacoes_tipos' && !c.filial);
      
      if (notifHabilitadas) setNotificacoesHabilitadas(notifHabilitadas.valor === 'true');
      if (notifTipos) setTiposNotificacoes(notifTipos.valor);
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      alert('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const carregarTermosRegras = async (filial: string) => {
    try {
      const dados = await ConfiguracoesService.buscarTodas(filial);
      const termosTitulo = dados.find(c => c.chave === 'termos_regras_titulo');
      const termosTexto = dados.find(c => c.chave === 'termos_regras_texto');
      const postos = dados.find(c => c.chave === 'postos_fiscais');
      const horario = dados.find(c => c.chave === 'horario_atendimento');
      const horarioNF = dados.find(c => c.chave === 'horario_recebimento_nf');
      
      if (termosTitulo) setTermosRegrasTitulo(termosTitulo.valor);
      if (termosTexto) setTermosRegrasTexto(termosTexto.valor);
      if (postos) setPostosFiscais(postos.valor);
      if (horario) setHorarioFuncionamento(horario.valor);
      if (horarioNF) setHorarioRecebimentoNF(horarioNF.valor);
    } catch (error) {
      console.error('Erro ao carregar termos e regras:', error);
    }
  };

  const carregarMotivosOcorrencia = async () => {
    try {
      const dados = await MotivosOcorrenciaService.buscarTodos();
      setMotivosOcorrencia(dados);
    } catch (error) {
      console.error('Erro ao carregar motivos:', error);
    }
  };

  const carregarConferentes = async () => {
    try {
      const dados = await ConfiguracoesService.buscarConferentes();
      setConferentes(dados);
    } catch (error) {
      console.error('Erro ao carregar conferentes:', error);
    }
  };

  const carregarAvisos = async () => {
    try {
      // Usar a primeira filial do usuário ou FILIAL_1 como padrão
      const filial = user?.filiais?.[0] || 'FILIAL_1';
      const dados = await AvisosService.buscarTodas(filial);
      setAvisos(dados);
    } catch (error) {
      console.error('Erro ao carregar avisos:', error);
    }
  };

  const salvarConfiguracao = async (chave: string, valor: string) => {
    try {
      await ConfiguracoesService.atualizar(chave, valor);
      alert('Configuração salva com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar configuração:', error);
      alert('Erro ao salvar configuração');
    }
  };

  const salvarNotificacoes = () => {
    salvarConfiguracao('notificacoes_habilitadas', notificacoesHabilitadas.toString());
    salvarConfiguracao('notificacoes_tipos', tiposNotificacoes);
  };

  const salvarTermosRegras = async () => {
    try {
      await ConfiguracoesService.atualizar('termos_regras_titulo', termosRegrasTitulo, filialAtual);
      await ConfiguracoesService.atualizar('termos_regras_texto', termosRegrasTexto, filialAtual);
      await ConfiguracoesService.atualizar('postos_fiscais', postosFiscais, filialAtual);
      await ConfiguracoesService.atualizar('horario_atendimento', horarioFuncionamento, filialAtual);
      await ConfiguracoesService.atualizar('horario_recebimento_nf', horarioRecebimentoNF, filialAtual);
      alert('Termos e regras salvos com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar termos e regras:', error);
      alert('Erro ao salvar termos e regras');
    }
  };

  const handleSalvarMotivo = async () => {
    try {
      if (editandoMotivo) {
        await MotivosOcorrenciaService.atualizar(editandoMotivo.id, {
          nome: motivoNome,
          descricao: motivoDescricao,
          ativo: true
        });
      } else {
        await MotivosOcorrenciaService.criar({
          nome: motivoNome,
          descricao: motivoDescricao,
          ativo: true
        });
      }
      
      setShowModalMotivo(false);
      setEditandoMotivo(null);
      setMotivoNome('');
      setMotivoDescricao('');
      carregarMotivosOcorrencia();
      alert('Motivo salvo com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar motivo:', error);
      alert('Erro ao salvar motivo');
    }
  };

  const handleEditarMotivo = (motivo: MotivoOcorrencia) => {
    setEditandoMotivo(motivo);
    setMotivoNome(motivo.nome);
    setMotivoDescricao(motivo.descricao || '');
    setShowModalMotivo(true);
  };

  const handleRemoverMotivo = async (id: string) => {
    if (!window.confirm('Deseja realmente remover este motivo?')) return;
    
    try {
      await MotivosOcorrenciaService.excluir(id);
      carregarMotivosOcorrencia();
      alert('Motivo removido com sucesso!');
    } catch (error) {
      console.error('Erro ao remover motivo:', error);
      alert('Erro ao remover motivo');
    }
  };

  const handleSalvarConferente = async () => {
    try {
      if (editandoConferente) {
        await ConfiguracoesService.atualizarConferente(
          editandoConferente.id,
          conferenteNome,
          conferenteFilial,
          true
        );
      } else {
        await ConfiguracoesService.criarConferente(conferenteNome, conferenteFilial);
      }
      
      setShowModalConferente(false);
      setEditandoConferente(null);
      setConferenteNome('');
      setConferenteFilial('');
      carregarConferentes();
      alert('Conferente salvo com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar conferente:', error);
      alert('Erro ao salvar conferente');
    }
  };

  const handleEditarConferente = (conferente: Conferente) => {
    setEditandoConferente(conferente);
    setConferenteNome(conferente.nome);
    setConferenteFilial(conferente.filial);
    setShowModalConferente(true);
  };

  const handleRemoverConferente = async (id: string) => {
    if (!window.confirm('Deseja realmente remover este conferente?')) return;

    try {
      await ConfiguracoesService.removerConferente(id);
      carregarConferentes();
      alert('Conferente removido com sucesso!');
    } catch (error) {
      console.error('Erro ao remover conferente:', error);
      alert('Erro ao remover conferente');
    }
  };

  const handleSalvarAviso = async () => {
    try {
      if (editandoAviso) {
        await AvisosService.atualizar(editandoAviso.id, {
          titulo: avisoTitulo,
          mensagem: avisoMensagem,
          lado: avisoLado,
          data: avisoData,
          status: avisoStatus
        });
      } else {
        await AvisosService.criar({
          titulo: avisoTitulo,
          mensagem: avisoMensagem,
          lado: avisoLado,
          data: avisoData,
          status: avisoStatus,
          filial: user?.filiais?.[0] || 'FILIAL_1'
        } as any);
      }

      setShowModalAviso(false);
      setEditandoAviso(null);
      setAvisoTitulo('');
      setAvisoMensagem('');
      setAvisoLado('esquerdo');
      setAvisoData(new Date().toISOString().split('T')[0]);
      setAvisoStatus('ativo');
      carregarAvisos();
      alert('Aviso salvo com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar aviso:', error);
      alert('Erro ao salvar aviso');
    }
  };

  const handleEditarAviso = (aviso: Aviso) => {
    setEditandoAviso(aviso);
    setAvisoTitulo(aviso.titulo);
    setAvisoMensagem(aviso.mensagem);
    setAvisoLado(aviso.lado);
    setAvisoData(aviso.data);
    setAvisoStatus(aviso.status);
    setShowModalAviso(true);
  };

  const handleRemoverAviso = async (id: string) => {
    if (!window.confirm('Deseja realmente remover este aviso?')) return;

    try {
      await AvisosService.deletar(id);
      carregarAvisos();
      alert('Aviso removido com sucesso!');
    } catch (error) {
      console.error('Erro ao remover aviso:', error);
      alert('Erro ao remover aviso');
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Configurações do Sistema</h1>
      
      {/* Abas de navegação principais */}
      <div className="flex space-x-1 bg-gray-100 rounded-lg p-1 mb-6">
        <button
          onClick={() => setAbaAtiva('gerais')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            abaAtiva === 'gerais'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Configurações Gerais
        </button>
        <button
          onClick={() => setAbaAtiva('cargas')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            abaAtiva === 'cargas'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Cargas
        </button>
      </div>

      {/* Subabas de Configurações Gerais */}
      {abaAtiva === 'gerais' && (
        <div className="flex space-x-1 bg-gray-50 rounded-lg p-1 mb-6">
          {podeVerNotificacoes && (
            <button
              onClick={() => setSubAbaGeral('notificacoes')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                subAbaGeral === 'notificacoes'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Notificações
            </button>
          )}
          {podeVerTermosRegras && (
            <button
              onClick={() => setSubAbaGeral('termos_regras')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                subAbaGeral === 'termos_regras'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Termos e Regras
            </button>
          )}
          {podeVerAvisos && (
            <button
              onClick={() => setSubAbaGeral('avisos')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                subAbaGeral === 'avisos'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Avisos e Lembretes
            </button>
          )}
        </div>
      )}

      {/* Subabas de Cargas */}
      {abaAtiva === 'cargas' && (
        <div className="flex space-x-1 bg-gray-50 rounded-lg p-1 mb-6">
          {podeVerConferentes && (
            <button
              onClick={() => setSubAbaCargas('conferentes')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                subAbaCargas === 'conferentes'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Conferentes
            </button>
          )}
          {podeVerMotivosOcorrencia && (
            <button
              onClick={() => setSubAbaCargas('motivos')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                subAbaCargas === 'motivos'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Motivos de Ocorrência
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12">
          <p className="text-gray-500">Carregando configurações...</p>
        </div>
      ) : (
        <>
          {/* Configurações de Notificações */}
          {abaAtiva === 'gerais' && subAbaGeral === 'notificacoes' && podeVerNotificacoes && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">Configurações de Notificações</h2>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Habilitar Notificações
                    </label>
                    <p className="text-sm text-gray-500">
                      Habilitar ou desabilitar notificações do sistema
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notificacoesHabilitadas}
                      onChange={(e) => setNotificacoesHabilitadas(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipos de Notificações
                  </label>
                  <p className="text-sm text-gray-500 mb-2">
                    Tipos de notificações habilitadas (separados por vírgula)
                  </p>
                  <textarea
                    value={tiposNotificacoes}
                    onChange={(e) => setTiposNotificacoes(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="solicitacao_aprovada,solicitacao_reprovada,pedido_digitado,carga_chegou"
                  />
                </div>

                <Button onClick={salvarNotificacoes} className="w-full">
                  Salvar Configurações de Notificações
                </Button>
              </div>
            </div>
          )}

          {/* Termos e Regras */}
          {abaAtiva === 'gerais' && subAbaGeral === 'termos_regras' && podeVerTermosRegras && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">Termos e Regras por Filial</h2>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Selecione a Filial para Editar
                </label>
                <select
                  value={filialAtual}
                  onChange={(e) => setFilialAtual(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {filiais.map(filial => (
                    <option key={filial.id} value={filial.id}>
                      {filial.nome}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Título dos Termos e Regras
                  </label>
                  <input
                    type="text"
                    value={termosRegrasTitulo}
                    onChange={(e) => setTermosRegrasTitulo(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Título que aparece no comprovante"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Texto dos Termos e Regras
                  </label>
                  <p className="text-sm text-gray-500 mb-2">
                    Texto completo que aparece no comprovante de agendamento
                  </p>
                  <textarea
                    value={termosRegrasTexto}
                    onChange={(e) => setTermosRegrasTexto(e.target.value)}
                    rows={8}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Digite os termos e regras aqui..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Postos Fiscais Obrigatórios
                  </label>
                  <p className="text-sm text-gray-500 mb-2">
                    Lista de postos fiscais obrigatórios (separados por ponto e vírgula)
                  </p>
                  <textarea
                    value={postosFiscais}
                    onChange={(e) => setPostosFiscais(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Delmiro Gouveia - Rodovia AL 423, KM 101;Novo Lino - Rodovia BR 101, KM 0;..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Horário de Funcionamento
                  </label>
                  <p className="text-sm text-gray-500 mb-2">
                    Horário de funcionamento da filial (formato: Segunda a Sexta: 08:00 às 18:00)
                  </p>
                  <input
                    type="text"
                    value={horarioFuncionamento}
                    onChange={(e) => setHorarioFuncionamento(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Segunda a Sexta: 08:00 às 18:00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Horário de Recebimento de Notas Fiscais
                  </label>
                  <p className="text-sm text-gray-500 mb-2">
                    Horário de recebimento de notas fiscais (formato: Segunda a Sexta: 08:00 às 17:00)
                  </p>
                  <input
                    type="text"
                    value={horarioRecebimentoNF}
                    onChange={(e) => setHorarioRecebimentoNF(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Segunda a Sexta: 08:00 às 17:00"
                  />
                </div>

                <Button onClick={salvarTermosRegras} className="w-full">
                  Salvar Termos e Regras
                </Button>
              </div>
            </div>
          )}

          {/* Motivos de Ocorrência */}
          {abaAtiva === 'cargas' && subAbaCargas === 'motivos' && podeVerMotivosOcorrencia && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Motivos de Ocorrência</h2>
                <Button onClick={() => {
                  setEditandoMotivo(null);
                  setMotivoNome('');
                  setMotivoDescricao('');
                  setShowModalMotivo(true);
                }}>
                  + Novo Motivo
                </Button>
              </div>
              
              <div className="space-y-2">
                {motivosOcorrencia.map((motivo) => (
                  <div key={motivo.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                    <div>
                      <h3 className="font-medium">{motivo.nome}</h3>
                      {motivo.descricao && (
                        <p className="text-sm text-gray-500">{motivo.descricao}</p>
                      )}
                      {!motivo.ativo && (
                        <span className="text-xs text-red-600">(Inativo)</span>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <Button variant="outline" size="sm" onClick={() => handleEditarMotivo(motivo)}>
                        Editar
                      </Button>
                      <Button variant="error" size="sm" onClick={() => handleRemoverMotivo(motivo.id)}>
                        Remover
                      </Button>
                    </div>
                  </div>
                ))}
                {motivosOcorrencia.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    Nenhum motivo de ocorrência cadastrado
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Conferentes */}
          {abaAtiva === 'cargas' && subAbaCargas === 'conferentes' && podeVerConferentes && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Conferentes</h2>
                <Button onClick={() => {
                  setEditandoConferente(null);
                  setConferenteNome('');
                  setConferenteFilial('');
                  setShowModalConferente(true);
                }}>
                  + Novo Conferente
                </Button>
              </div>

              <div className="space-y-2">
                {conferentes.map((conferente) => (
                  <div key={conferente.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                    <div>
                      <h3 className="font-medium">{conferente.nome}</h3>
                      <p className="text-sm text-gray-500">Filial: {conferente.filial}</p>
                      {!conferente.ativo && (
                        <span className="text-xs text-red-600">(Inativo)</span>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <Button variant="outline" size="sm" onClick={() => handleEditarConferente(conferente)}>
                        Editar
                      </Button>
                      <Button variant="error" size="sm" onClick={() => handleRemoverConferente(conferente.id)}>
                        Remover
                      </Button>
                    </div>
                  </div>
                ))}
                {conferentes.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    Nenhum conferente cadastrado
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Avisos e Lembretes */}
          {abaAtiva === 'gerais' && subAbaGeral === 'avisos' && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Avisos e Lembretes</h2>
                <Button onClick={() => {
                  setEditandoAviso(null);
                  setAvisoTitulo('');
                  setAvisoMensagem('');
                  setAvisoLado('esquerdo');
                  setAvisoData(new Date().toISOString().split('T')[0]);
                  setAvisoStatus('ativo');
                  setShowModalAviso(true);
                }}>
                  + Novo Aviso
                </Button>
              </div>

              <div className="space-y-2">
                {avisos.map((aviso) => (
                  <div key={aviso.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                    <div>
                      <h3 className="font-medium">{aviso.titulo}</h3>
                      <p className="text-sm text-gray-500">{aviso.mensagem}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-400">Lado: {aviso.lado}</span>
                        <span className="text-xs text-gray-400">Data: {aviso.data}</span>
                        {aviso.status === 'inativo' && (
                          <span className="text-xs text-red-600">(Inativo)</span>
                        )}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Button variant="outline" size="sm" onClick={() => handleEditarAviso(aviso)}>
                        Editar
                      </Button>
                      <Button variant="error" size="sm" onClick={() => handleRemoverAviso(aviso.id)}>
                        Remover
                      </Button>
                    </div>
                  </div>
                ))}
                {avisos.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    Nenhum aviso cadastrado
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal de Motivo */}
      <Modal
        isOpen={showModalMotivo}
        onClose={() => {
          setShowModalMotivo(false);
          setEditandoMotivo(null);
          setMotivoNome('');
          setMotivoDescricao('');
        }}
        title={editandoMotivo ? 'Editar Motivo' : 'Novo Motivo'}
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome do Motivo
            </label>
            <input
              type="text"
              value={motivoNome}
              onChange={(e) => setMotivoNome(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Ex: Atraso na entrega"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descrição (opcional)
            </label>
            <textarea
              value={motivoDescricao}
              onChange={(e) => setMotivoDescricao(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Descrição detalhada do motivo"
            />
          </div>
          <Button onClick={handleSalvarMotivo} className="w-full">
            {editandoMotivo ? 'Atualizar' : 'Criar'} Motivo
          </Button>
        </div>
      </Modal>

      {/* Modal de Conferente */}
      <Modal
        isOpen={showModalConferente}
        onClose={() => {
          setShowModalConferente(false);
          setEditandoConferente(null);
          setConferenteNome('');
          setConferenteFilial('');
        }}
        title={editandoConferente ? 'Editar Conferente' : 'Novo Conferente'}
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome do Conferente
            </label>
            <input
              type="text"
              value={conferenteNome}
              onChange={(e) => setConferenteNome(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Nome completo"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filial
            </label>
            <select
              value={conferenteFilial}
              onChange={(e) => setConferenteFilial(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Selecione uma filial</option>
              <option value="FILIAL_1">FILIAL_1</option>
              <option value="FILIAL_2">FILIAL_2</option>
              <option value="FILIAL_3">FILIAL_3</option>
            </select>
          </div>
          <Button onClick={handleSalvarConferente} className="w-full">
            {editandoConferente ? 'Atualizar' : 'Criar'} Conferente
          </Button>
        </div>
      </Modal>

      {/* Modal de Aviso */}
      <Modal
        isOpen={showModalAviso}
        onClose={() => {
          setShowModalAviso(false);
          setEditandoAviso(null);
          setAvisoTitulo('');
          setAvisoMensagem('');
          setAvisoLado('esquerdo');
          setAvisoData(new Date().toISOString().split('T')[0]);
          setAvisoStatus('ativo');
        }}
        title={editandoAviso ? 'Editar Aviso' : 'Novo Aviso'}
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Título do Aviso
            </label>
            <input
              type="text"
              value={avisoTitulo}
              onChange={(e) => setAvisoTitulo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Ex: Feriado"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mensagem
            </label>
            <textarea
              value={avisoMensagem}
              onChange={(e) => setAvisoMensagem(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Descrição do aviso"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Lado
            </label>
            <select
              value={avisoLado}
              onChange={(e) => setAvisoLado(e.target.value as 'esquerdo' | 'direito')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="esquerdo">Esquerdo (Avisos)</option>
              <option value="direito">Direito (Lembretes)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data
            </label>
            <input
              type="date"
              value={avisoData}
              onChange={(e) => setAvisoData(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={avisoStatus}
              onChange={(e) => setAvisoStatus(e.target.value as 'ativo' | 'inativo')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="ativo">Ativo</option>
              <option value="inativo">Inativo</option>
            </select>
          </div>
          <Button onClick={handleSalvarAviso} className="w-full">
            {editandoAviso ? 'Atualizar' : 'Criar'} Aviso
          </Button>
        </div>
      </Modal>
    </div>
  );
};

const ConfiguracoesProtegido = () => (
  <AbaProtegida nomeAba="Configurações">
    <Configuracoes />
  </AbaProtegida>
);

export default ConfiguracoesProtegido;
